import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { uiLogger } from "../../utils/frontendLogger";

interface State {
  hasError: boolean;
  error:    Error | null;
}

/**
 * 🛡️ PlatformErrorBoundary
 * Wraps any subtree so a render crash shows a recovery screen
 * instead of blanking the entire shell.
 */
export class PlatformErrorBoundary extends React.Component<
  { children: React.ReactNode; moduleName?: string },
  State
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    uiLogger.error("Platform render crash", {
      module:    this.props.moduleName ?? "unknown",
      message:   error.message,
      component: info.componentStack?.split("\n")[1]?.trim() ?? "",
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 p-8">
        {/* Icon */}
        <div className="h-16 w-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center">
          <AlertTriangle size={28} className="text-red-400" />
        </div>

        {/* Message */}
        <div className="text-center max-w-sm">
          <p className="text-lg font-bold text-text">
            {this.props.moduleName
              ? `${this.props.moduleName} encountered an error`
              : "Something went wrong"}
          </p>
          <p className="text-sm text-muted mt-1">
            A render error occurred in this module. Other parts of the platform
            remain unaffected.
          </p>
          {this.state.error?.message && (
            <p className="text-[10px] font-mono text-muted/60 mt-3 bg-slate-50 px-4 py-2
                          rounded-xl border border-border inline-block text-left break-all">
              {this.state.error.message}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={this.handleReset}
            className="btn-primary"
          >
            <RefreshCw size={14} /> Try again
          </button>
          <button
            onClick={() => (window.location.href = "/platform")}
            className="flex items-center gap-2 px-5 py-2 border border-border text-sm
                       font-semibold text-muted rounded-xl hover:bg-slate-50 transition-colors"
          >
            <Home size={14} /> Go to Cockpit
          </button>
        </div>
      </div>
    );
  }
}
